import Phaser from 'phaser';
import { isBreachable, type LevelDef, type NodeDef, type SimEvent, type SimResult } from '../sim/types';
import { NodeView } from './NodeView';
import { WireView } from './WireView';
import { Backdrop } from './Backdrop';
import { TIMING } from './palette';
import { Sfx } from '../audio/sfx';
import { t, FONT_STACK } from '../i18n';

export type PlaybackHandlers = {
  onComplete: (result: SimResult) => void;
};

export class GraphView {
  readonly nodes = new Map<string, NodeView>();
  readonly wires = new Map<string, WireView>();
  private scene: Phaser.Scene;
  private level: LevelDef;
  private sfx: Sfx;
  private backdrop?: Backdrop;
  private playbackTimer?: Phaser.Time.TimerEvent;
  private nodeDefs = new Map<string, NodeDef>();
  private requiredRemaining = 0;
  private requiredTotal = 0;

  constructor(scene: Phaser.Scene, level: LevelDef, sfx: Sfx, backdrop?: Backdrop) {
    this.scene = scene;
    this.level = level;
    this.sfx = sfx;
    this.backdrop = backdrop;
    for (const n of level.nodes) this.nodeDefs.set(n.id, n);
    this.requiredTotal = level.objective.requiredNodeIds.length;
    this.requiredRemaining = this.requiredTotal;

    // Build initial shielded set so NodeView can render the shield ring
    const initiallyShielded = new Set<string>();
    for (const n of level.nodes) {
      if (n.type === 'shield' && n.shields) {
        for (const id of n.shields) initiallyShielded.add(id);
      }
    }

    // Build wires first so they render under nodes
    const nodeIndex = new Map<string, { x: number; y: number }>();
    for (const n of level.nodes) nodeIndex.set(n.id, { x: n.x, y: n.y });

    for (const w of level.wires) {
      const from = nodeIndex.get(w.a)!;
      const to = nodeIndex.get(w.b)!;
      const view = new WireView(scene, w, from, to);
      this.wires.set(w.id, view);
    }

    for (const n of level.nodes) {
      const view = new NodeView(scene, n, initiallyShielded.has(n.id));
      this.nodes.set(n.id, view);
    }
  }

  onNodeTap(handler: (nodeId: string) => void) {
    // Build adjacency for quick lookup of wires touching each node
    const adj = new Map<string, string[]>();
    for (const n of this.level.nodes) adj.set(n.id, []);
    for (const w of this.level.wires) {
      adj.get(w.a)?.push(w.id);
      adj.get(w.b)?.push(w.id);
    }

    for (const [id, view] of this.nodes) {
      const def = this.nodeDefs.get(id);
      // Only plain access nodes are breach points. Tapping a mechanic node
      // (required/core/protected/relay/shield/splitter/capacitor) does nothing
      // but a small "denied" nudge — it is part of the machine, not an entry.
      if (def && isBreachable(def)) {
        view.onPointerDown(() => handler(id));
      } else {
        view.onPointerDown(() => view.denyBreach(this.scene));
      }
      view.onHover(
        () => {
          for (const wireId of adj.get(id) ?? []) {
            this.wires.get(wireId)?.setHovered(true);
          }
        },
        () => {
          for (const wireId of adj.get(id) ?? []) {
            this.wires.get(wireId)?.setHovered(false);
          }
        },
      );
    }
  }

  setAllInteractive(enabled: boolean) {
    for (const view of this.nodes.values()) view.setInteractiveEnabled(enabled);
  }

  private skipRequested = false;

  /**
   * Fast-forward the current cascade to its end. Applies any remaining events
   * immediately. Lets a "cruising" player skip the animation instead of being
   * stuck watching a multi-second playback (which reads as a freeze).
   */
  requestSkip() {
    this.skipRequested = true;
  }

  /** Plays a sim result back through the visuals. */
  playback(result: SimResult, handlers: PlaybackHandlers) {
    this.setAllInteractive(false);
    this.skipRequested = false;
    const events = result.events;
    if (events.length === 0) {
      handlers.onComplete(result);
      return;
    }

    let i = 0;
    let completed = false;
    const startedAt = this.scene.time.now;
    const applyUpTo = (limit: number) => {
      while (i < limit) {
        try {
          this.applyEvent(events[i]);
        } catch (err) {
          // Never let one bad event freeze the cascade — log and skip.
          // eslint-disable-next-line no-console
          console.error('[nodefall] applyEvent failed:', events[i], err);
        }
        i++;
      }
    };
    const complete = (delayMs: number) => {
      if (completed) return;
      completed = true;
      this.playbackTimer?.remove(false);
      this.playbackTimer = undefined;
      this.scene.time.delayedCall(delayMs, () => handlers.onComplete(result));
    };

    this.playbackTimer = this.scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        if (this.skipRequested) {
          applyUpTo(events.length);
          complete(120);
          return;
        }
        const elapsed = this.scene.time.now - startedAt;
        let limit = i;
        while (limit < events.length && events[limit].tick * TIMING.tickMs <= elapsed) limit++;
        applyUpTo(limit);
        if (i >= events.length) {
          // give the last animations a moment to finish
          complete(TIMING.tickMs + 200);
        }
      },
    });
  }

  stopPlayback() {
    this.playbackTimer?.remove(false);
    this.playbackTimer = undefined;
  }

  private applyEvent(e: SimEvent) {
    switch (e.kind) {
      case 'tap': {
        // visual feedback for the breach itself happens via the collapse event that follows
        this.sfx.tap();
        const def = this.nodeDefs.get(e.nodeId);
        if (def && this.backdrop) {
          this.backdrop.ripple(def.x, def.y, 0x88e8ff, 1.4);
        }
        // cinematic target reticle on the tapped node
        this.nodes.get(e.nodeId)?.playTapReticle(this.scene);
        // gentle zoom-kick for impact
        this.cameraKick(0.012);
        break;
      }
      case 'node_collapse': {
        const n = this.nodes.get(e.nodeId);
        const def = this.nodeDefs.get(e.nodeId);
        n?.setCollapsed(this.scene);
        this.sfx.collapse();

        if (def) {
          const isRequired = def.type === 'required';
          const isCore = def.type === 'core';
          // ripple at world coord
          if (this.backdrop) {
            const power = isCore ? 1.8 : isRequired ? 1.2 : 0.7;
            const tint =
              def.type === 'core' ? 0xee9aff :
              def.type === 'required' ? 0x6cf0ff :
              def.type === 'protected' ? 0xff5577 :
              def.type === 'shield' ? 0x88ffb0 :
              def.type === 'relay' ? 0xffd070 :
              def.type === 'splitter' ? 0xff99dd :
              def.type === 'capacitor' ? 0xfff088 :
              0x8aa6ff;
            this.backdrop.ripple(def.x, def.y, tint, power);
          }
          if (isRequired || isCore) this.cameraKick(0.018);

          if (isRequired) {
            this.requiredRemaining = Math.max(0, this.requiredRemaining - 1);
            this.spawnCallout(def.x, def.y, t('callout_required_crashed'), '#6cf0ff');
            this.scene.events.emit('nodefall:required-collapsed', {
              remaining: this.requiredRemaining,
              total: this.requiredTotal,
            });
          } else if (isCore) {
            this.spawnCallout(def.x, def.y, t('callout_core_breached'), '#dd99ff');
            this.scene.cameras.main.shake(380, 0.014);
            this.scene.events.emit('nodefall:core-collapsed');
          }
        }
        break;
      }
      case 'pulse_emit': {
        const w = this.wires.get(e.wireId);
        if (!w) break;
        const travelMs = (e.arriveTick - e.tick) * TIMING.tickMs;
        w.playPulse({ fromId: e.fromId, toId: e.toId }, travelMs);
        this.sfx.surge();
        break;
      }
      case 'pulse_arrive': {
        const n = this.nodes.get(e.toId);
        if (!n) break;
        n.showCharge(e.chargeAfter, e.thresholdAt);
        n.flashCharge(this.scene);
        break;
      }
      case 'charge_decay': {
        const n = this.nodes.get(e.nodeId);
        if (!n) break;
        n.showCharge(0, 1);
        n.flashDecay(this.scene);
        break;
      }
      case 'wire_open': {
        const w = this.wires.get(e.wireId);
        w?.open_(this.scene);
        this.sfx.unlock();
        const def = this.nodeDefs.get(e.byRelayId);
        if (def) this.spawnCallout(def.x, def.y - 30, t('callout_relay_opened'), '#ffd070');
        break;
      }
      case 'shield_drop': {
        for (const id of e.protectedIds) {
          this.nodes.get(id)?.dropShield(this.scene);
        }
        this.sfx.unlock();
        const def = this.nodeDefs.get(e.shieldId);
        if (def) this.spawnCallout(def.x, def.y, 'SHIELD DROPPED', '#88ffb0');
        break;
      }
      case 'wire_burn': {
        this.wires.get(e.wireId)?.burn(this.scene);
        break;
      }
      case 'core_pulse': {
        // visual hint already covered by pulse_arrive; nothing extra here
        break;
      }
      case 'protected_overload': {
        this.nodes.get(e.nodeId)?.flashProtectedOverload(this.scene);
        this.sfx.alarm();
        const def = this.nodeDefs.get(e.nodeId);
        if (def) this.spawnCallout(def.x, def.y - 30, 'PROTECTED HIT', '#ff5577');
        this.hitStop(120);
        this.backdrop?.flash(0xff2244, 0.45, 600);
        break;
      }
      case 'forbidden_trigger': {
        this.wires.get(e.wireId)?.triggerForbidden(this.scene);
        this.sfx.alarm();
        this.hitStop(120);
        this.backdrop?.flash(0xff2244, 0.5, 600);
        this.backdrop?.scanlineBurst();
        break;
      }
    }
  }

  /**
   * Floating one-line callout that rises off a node and fades. Lives in
   * world space so it pans with the camera. Subtle, monospace, type-tinted.
   */
  private spawnCallout(worldX: number, worldY: number, text: string, color: string) {
    const t = this.scene.add.text(worldX, worldY - 75, text, {
      fontFamily: 'ui-monospace, monospace',
      fontSize: '21px',
      color,
      fontStyle: 'bold',
    }).setOrigin(0.5, 0.5).setDepth(800);
    t.setShadow(0, 0, '#000000', 9, true, true);
    this.scene.tweens.add({
      targets: t,
      y: worldY - 144,
      alpha: { from: 1, to: 0 },
      duration: 1100,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  /**
   * Tiny zoom punch then settle. Adds heft to collapses without disorienting
   * the player like a shake would.
   */
  private cameraKick(amount: number) {
    const cam = this.scene.cameras.main;
    const base = cam.zoom;
    this.scene.tweens.add({
      targets: cam,
      zoom: base * (1 + amount),
      duration: 70,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Time-scale dip — freezes the simulation feel for a moment so the
   * player registers a high-stakes event (forbidden trigger, protected
   * overload). Restores to 1 automatically.
   */
  private hitStop(ms: number) {
    // Only scale TWEENS, not the scene clock. Tween dilation gives the
    // slow-mo feel; leaving time.timeScale at 1 means scene.time.delayedCall
    // still runs at normal speed and gets cleaned up on shutdown.
    this.scene.tweens.timeScale = 0.18;
    this.scene.time.delayedCall(ms, () => {
      this.scene.tweens.timeScale = 1;
    });
  }

  destroy() {
    this.stopPlayback();
    for (const n of this.nodes.values()) n.destroy();
    for (const w of this.wires.values()) w.destroy();
    this.nodes.clear();
    this.wires.clear();
  }
}
